/**
 * Retailer identity for product cards (Stage Z PR B).
 *
 * The BUY panel previously rendered the literal word "Source", which hides which
 * retailer the customer is actually buying from. Every card must name the real
 * retailer and show its logo.
 *
 * Resolution order:
 *   1. the live retailer directory (name + hosted favicon), when available;
 *   2. a static fallback for known registry ids, so a card still reads correctly
 *      before the directory loads or when it fails;
 *   3. a readable title-cased id derived from the source id itself.
 *
 * Nothing here invents a logo: an unknown retailer gets its initial, never
 * another retailer's artwork.
 */

/** Known registry ids → display name. Ids are the canonical `*_com` forms. */
export const RETAILER_NAMES = Object.freeze({
  dermstore_com: "Dermstore",
  fivebelow_com: "Five Below",
  chewy_com: "Chewy",
  target_com: "Target",
  walmart_com: "Walmart",
  michaels_com: "Michaels",
  officesupply_com: "Office Supply",
  beautybrands_com: "Beauty Brands",
  sallybeauty_com: "Sally Beauty",
  ulta_com: "Ulta",
  sephora_com: "Sephora",
  walgreens_com: "Walgreens",
  cvs_com: "CVS",
  homedepot_com: "Home Depot",
  lowes_com: "Lowe's",
  bedbathandbeyond_com: "Bed Bath & Beyond",
  kohls_com: "Kohl's",
  nordstrom_com: "Nordstrom",
  macys_com: "Macy's",
  petco_com: "Petco",
  petsmart_com: "PetSmart",
  vitamin_shoppe_com: "Vitamin Shoppe",
  iherb_com: "iHerb",
  costco_com: "Costco",
  samsclub_com: "Sam's Club",
  bjs_com: "BJ's",
});

/** Brand-ish ids where title-casing alone reads badly. */
const TITLE_CASE_EXCEPTIONS = Object.freeze({
  iherb: "iHerb",
  cvs: "CVS",
  ulta: "Ulta",
  sephora: "Sephora",
});

/**
 * Readable retailer name for a source id.
 *
 * `dermstore_com` → "Dermstore", `dermstore` → "Dermstore",
 * `iherb_com` → "iHerb", unknown `foo_bar_com` → "Foo Bar".
 */
export function retailerName(sourceId, directory = null) {
  if (!sourceId) return "";
  const id = String(sourceId).trim().toLowerCase();
  if (!id) return "";

  // 1. Live directory wins — it is the authoritative catalogue.
  if (Array.isArray(directory)) {
    const found = directory.find(
      (s) => String(s?.source_id || "").toLowerCase() === id,
    );
    if (found?.name) return String(found.name).trim();
  }

  // 2. Static fallback for known ids.
  if (RETAILER_NAMES[id]) return RETAILER_NAMES[id];

  // 3. Derive from the id, dropping the TLD.
  const base = id.replace(/_(com|co|net|org)$/i, "");
  const words = base.split(/[_\-\s]+/).filter(Boolean);
  if (!words.length) return "";
  const first = TITLE_CASE_EXCEPTIONS[words[0]] || words[0].charAt(0).toUpperCase() + words[0].slice(1);
  return [first, ...words.slice(1).map((w) => w.charAt(0).toUpperCase() + w.slice(1))].join(" ");
}

/** Directory row for a source id, or null (never fabricated). */
export function retailerRecord(sourceId, directory = null) {
  if (!sourceId || !Array.isArray(directory)) return null;
  const id = String(sourceId).trim().toLowerCase();
  return (
    directory.find((s) => String(s?.source_id || "").toLowerCase() === id) || null
  );
}

/**
 * Labels for the two sides of a comparison.
 *
 * SELL is always Walmart — never a generic "Sell".
 * BUY names the actual retailer when known, and says so plainly when not.
 */
export function panelLabels({ sourceId, directory } = {}) {
  const name = retailerName(sourceId, directory);
  return {
    buyLabel: name || "Source retailer",
    buyIsKnown: Boolean(name),
    sellLabel: "Walmart",
  };
}

/** Initial used by the favicon fallback tile. Never another retailer's logo. */
export function retailerInitialFor(sourceId, directory = null) {
  const name = retailerName(sourceId, directory);
  return (name.trim().charAt(0) || "?").toUpperCase();
}
